/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {List, makeStyles} from '@material-ui/core';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {cirusPrimaryProperties, GenericObject} from '@ion/core/src/types/entities';
import CardItemRow from '@ion/dls/src/layout/CardItemRow';
import {DLSTheme} from '@ion/dls/src/types/types';
import {formatCirusData} from './spreaderUtils';

export interface CirusCardListContentProps {
  measures: GenericObject
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  list : {
    paddingBottom : '0px'
  }
}));

export const CirusCardListContent = ({measures}: CirusCardListContentProps) => {
  const classes = useStyles();
  const noValue = translate('common:notAvailable');

  /**
   * getCirusProperties is a function that compares the properties included
   * in the measures object with the properties in cirusPrimaryProperties.
   * If some properties are not included in the measures object, we need to return
   * the difference so that all properties are displayed in the SpreaderMessageAccordion component.
   */
  const getCirusProperties = useCallback(known => {
    const properties = {...known};
    Object.keys(cirusPrimaryProperties).forEach(property => {
      properties[property] = known[property] || cirusPrimaryProperties[property];
    });
    return Object.keys(properties).sort().reverse();
  }, [measures]);

  const cirusProperties = getCirusProperties(measures);

  return (
    <List className={classes.list}>
      {cirusProperties.map((key: string) => {
        const value = measures[key];
        return (
          <CardItemRow key={key}
            label={translate(`tracking:spreader.CIRUS.${key}`)}
            value={!isNil(value) ? formatCirusData(key, value) : noValue}
            isCenterAligned={false}
            isBottomMargin={false}
            isTextRightAligned
            isCondensedVariant />
        );
      }
      )}
    </List>
  );
};

export default CirusCardListContent;
